import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

// Components
import Max2Masterdata from './masterdata';
import Max2Media from './media';
import Max2Texts from './texts';
import Max2Transfers from './transfers';
import Max2Contracts from './contracts';
import Max2Exports from './exports';
import Max2Allotments from './allotments';
import Max2History from '../system/history';
import AjaxController from '../system/ajaxController';

// Redux slices
import { startEdit, confimEdit, abortEdit, setObjectID, setObjectData } from './objectSlice';
import { maximizeSection, minimizeSection, hideObjectSidebar, setMetaData } from './objectSlice'
import { setActivePage, setUser, setToken } from '../../appSlice';

/* Snackbar
 * https://notistack.com/api-reference
 */
import { useSnackbar } from 'notistack';

// SemanticUI components
import { Segment, SidebarPushable, Sidebar, SidebarPusher, Icon, Grid, GridRow, GridColumn, Label, Header, HeaderContent, Menu, MenuItem, Container, Divider, Button, Modal, Form, FormInput } from 'semantic-ui-react';

// Style
import './object.css';



/**
 * Component to display the response after copying an object.
 * Shows a success message with a link to open the new object.
 * 
 * @param {Object} param0 - Component props
 * @param {Object} param0.response - Response data from the copy operation
 * @param {Function} param0.closeModalFunc - Function to close the modal
 * @returns {JSX.Element} The copy response component
 */
const Max2CopyObjectResponse = ({ response, closeModalFunc }) => {
  const dispatch = useDispatch();

  // Translations
  const { t } = useTranslation();

  if (!response) {
    return <></>;
  }



  /**
   * 
   * @param {integer} oid 
   * @param {object} new_object
   */
  const handleOpenObjectClick = (oid, new_object) => {
    dispatch(hideObjectSidebar());
    dispatch(abortEdit());

    dispatch(setObjectID(oid));
    dispatch(setObjectData(new_object));
    
    closeModalFunc();
  }


  
  if (response?.new_id) {
    return (
      <Segment>
        <Header as='h2' textAlign='center'>
          <Icon name='check circle outline' />
          <HeaderContent>
            {t('object.copy.success.title')}
          </HeaderContent>
        </Header>

        <Container style={{textAlign: 'center', marginTop: '20px', marginBottom: '20px'}}>
          <p style={{textAlign: 'center'}}>{t('object.copy.success.text', { id: response?.new_id })}</p>
          <Button primary circular style={{width:'200px', margin: '5px'}} onClick={()=>handleOpenObjectClick(response?.new_id, response?.object)}>{t('object.copy.success.openNew')}</Button>
        </Container>
      </Segment>
    );
  }


  return (
    <Segment>
      <br />
      <Header as='h2' textAlign='center'>
        <Icon name='times circle outline' />
        <HeaderContent>
          {t('object.copy.error.title')}
        </HeaderContent>
      </Header>
      <br />
    </Segment>
  );
}



/*
 * Component to display the print view of an object.
 * Shows masterdata, texts, and media in a printable format.
 * 
 * @param {Object} param0 - Component props
 * @param {Object} param0.object - Object data to print
 * @returns {JSX.Element} The print view component
 */
const Max2ObjectPrintView = ({object}) => {
  // Translations
  const { t } = useTranslation();
  
  return (
    <>
    <div style={{textAlign: 'left'}}>
      <Max2Masterdata columns={1} />
      <div class="page-break-after"></div>
      <Divider horizontal>{t('object.print.texts')}</Divider>
      <Max2Texts />
      <div class="page-break-after"></div>
      <Divider horizontal>{t('object.print.images')}</Divider>
      <Max2Media />
    </div>
    </>
  );
}




/**
 * 
 * @returns 
 */
const Max2PageObject = () => {
  // lockaler State
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadad, setIsLoaded] = useState(false);
  const isFetching = useRef(false); // Ref, um mehrfachen Request zu verhindern
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyOptions, setCopyOptions] = useState({code: '', name: ''});
  const [copyObject, setCopyObject] = useState(false);
  const [copyResponse, setCopyResponse] = useState(false);
  
  // Globaler State
  const dispatch = useDispatch();
  const isSidebar     = useSelector((state) => state.object.isSidebar);
  const backendUrl    = useSelector((state) => state.app.backend.baseProtocol) + '://' + useSelector((state) => state.app.backend.url);
  const stateSections = useSelector((state) => state.object.sections);
  const objectID      = useSelector((state) => state.app.objectId);
  const object        = useSelector((state) => state.object.data);
  const dataChanges   = useSelector((state) => state.object.dataChanges);
  const meta          = useSelector((state) => state.object.meta);

  // Translations
  const { t } = useTranslation();

  // Snackbar
  const { enqueueSnackbar } = useSnackbar();


  
  /**
   * 
   * @param {string} section 
   */
  const handleEditModeClick = async (section) => {

    // init AJAX controller
    const api = new AjaxController(backendUrl, () => {
      dispatch(setActivePage('START'));
      dispatch(setUser(null));
      dispatch(setToken(null));
    });

    // set new lock
    let response;
    try {
      /*response = await api.put('/hotel/'+objectID+'/lock');
      if (response.status === 'error' && typeof response.lock !== 'undefined') {
        const min_remaining = Math.ceil(response.lock.time_remaining / 60);
        enqueueSnackbar(t('object.lock.inUse', { user: response.lock.uname, minutes: min_remaining }), { variant: 'error' });
        setCopyObject(false);
        return;
      }

      // start edit mode
      if (response.status === 'success') {*/
        dispatch(startEdit(section));
      //}
    } catch (error) {
      enqueueSnackbar(t('object.lock.setError'), { variant: 'error' });
      setCopyObject(false);
      return;
    }
  }



  /**
   * 
   * @param {string} section 
   */
  const handleAbortEditModeClick = (section) => {
    // remove lock when no changes are pending
    if (dataChanges.length === 0) {
      removeLock();
    }

    dispatch(abortEdit(section))
  }



  /**
   * Remove lock function
   * 
   * @async 
   */
  const removeLock = async () => {
    // init AJAX controller
    const api = new AjaxController(backendUrl, () => {
      dispatch(setActivePage('START'));
      dispatch(setUser(null));
      dispatch(setToken(null));
    });

    // remove lock
    let response;
    try {
      /*response = await api.delete('/hotel/'+objectID+'/unlock');
      if (response.status !== 'success') {
        enqueueSnackbar(t('object.lock.releaseError'), { variant: 'error' });
        return;
      }*/
    } catch (error) {
      enqueueSnackbar(t('object.lock.releaseError'), { variant: 'error' });
      return;
    }
  }



  /**
   * 
   * @param {string} section 
   */
  const handleConfirmEditModeClick = (section) => {
    dispatch(confimEdit(section));
  }


  /**
   * Copy object function
   * 
   * @async
   */
  const copyObjectClick = useCallback(async (objectID) => {
    if (!objectID) {
      enqueueSnackbar(t('object.copy.errors.noObjectSelected'), { variant: 'error' });
      return;
    }

    // Check if backendUrl is defined
    if (!backendUrl) {
      enqueueSnackbar(t('object.copy.errors.missingBackendUrl'), { variant: 'error' });
      return;
    }

    // Check if objectID is valid
    if (isNaN(objectID)) {
      enqueueSnackbar(t('object.copy.errors.invalidId'), { variant: 'error' });
      return;
    }

    // check new Code
    if (copyOptions.code === object.Product.Code) {
      enqueueSnackbar(t('object.copy.errors.sameCode'), { variant: 'error' });
      return;
    }

    // show loading animation
    setCopyObject(true);

    // Controller for AJAX requests
    const api = new AjaxController(backendUrl, () => {
      dispatch(setActivePage('START'));
      dispatch(setUser(null));
      dispatch(setToken(null));
    });

    // check if new code is already in use
    let responseCheck;
    try {
      const query = {
        filter: { code: { value: copyOptions.code, enabled: true } }
      }
      responseCheck = await api.get('/count/' + btoa(JSON.stringify(query)).replaceAll('=', '_'));
      if (responseCheck) {
        enqueueSnackbar(t('object.copy.errors.codeInUse'), { variant: 'error' });
        return;
      }
    } catch (error) {
      enqueueSnackbar(t('object.copy.errors.filterFailed'), { variant: 'error' });
      setCopyObject(false);
      return;
    }
    

    // send create pdf request
    let response;
    try {
      response = await api.post('/hotel/' + objectID + '/copy', {...copyOptions, id: objectID});
      console.log('response', response)
      setCopyResponse(response);
    } catch (error) {
      enqueueSnackbar(t('object.copy.errors.copyFailed'), { variant: 'error' });
      setCopyObject(false);
      return;
    }
    
    // hide loading animation
    setCopyObject(false);

  }, [backendUrl, copyOptions, object, dispatch, enqueueSnackbar, t]);



  /**
   * 
   */
  const handlePrintViewClick = () => {
    const newWindow = window.open("printpreview.html", "_blank");

    if (!newWindow) {
      console.log("Neues Fenster konnte nicht geöffnet werden.");
      return;
    }

    window.setTimeout(() => {
      populatePrintPreview(newWindow);
    }, 500);
  }

  /**
   *
   * @param {Window} newWindow
   */
  const populatePrintPreview = (newWindow) => {
    const targetDiv = newWindow.document.getElementById("printpreview_content");
    if (targetDiv) {
      targetDiv.innerHTML = document.getElementById("print_preview_content").innerHTML;
    } else {
      console.log("Ziel-Container nicht gefunden.");
    }
  }


  // Load object data on first render
  useEffect(() => {
    const api = new AjaxController(backendUrl, () => {
          dispatch(setActivePage('START'));
          dispatch(setUser(null));
          dispatch(setToken(null));
        });
    
    const fetchData = async (objectID) => {
      if (isFetching.current) return; // Überspringe, falls bereits ein Request läuft
      isFetching.current = true;

      try {
        const response = await api.get('/hotel/' + objectID);

        // display results
        setIsLoading(false);
        setIsLoaded(true);
        if (typeof response.object === 'object') {
          dispatch(setObjectData(response.object));
        }
        if (typeof response.meta === 'object') {
          dispatch(setMetaData(response.meta));
        }
      } catch (err) {
        //setError(err.message);
        console.error(err.message)
      }
    };

    if (objectID && objectID !== '__new__' && !isLoading) {
      fetchData(objectID);
    }

    if (objectID && objectID === '__new__') {
      setIsLoading(false);
      setIsLoaded(true);
    }

  }, [backendUrl, dispatch, isLoading, objectID]);


  // Determine how many main sections are maximized
  let mainSectionsMax = 0;
  if (stateSections.Media.maximized) mainSectionsMax++;
  if (stateSections.Texts.maximized) mainSectionsMax++;
  if (stateSections.Allotments.maximized) mainSectionsMax++;
  if (stateSections.History.maximized) mainSectionsMax++;
  if (objectID && objectID === '__new__') mainSectionsMax = 1;
  const sideSectionsWidth = mainSectionsMax > 1 ? 'S' : 'L';

  // Available sections
  /*const NumberOfImages    = object?.Images ? ' (' + object.Images.length + ')' : '';
  const NumberOfTransfers = object?.Transfers ? ' (' + object.Transfers.length + ')' : '';
  const NumberOfContracts = object?.Contracts ? ' (' + object.Contracts.length + ')' : '';
  const NumberOfExports   = '(' + (object?.Exports?.PDF ? object?.Exports?.PDF.length : '-') + '/' + (object?.Exports?.EDF ? object?.Exports?.EDF.length : '-') + ')'; */
  const availableSections = [
    { key: 'Masterdata',  header: t('object.sections.masterdata'), editMode: true,  confirmEdit: true,  content: <Max2Masterdata columns={3} /> },
    { key: 'Texts',       header: t('object.sections.texts'),      editMode: true,  confirmEdit: true,  content: <Max2Texts /> },
    { key: 'Media',       header: t('object.sections.media',      { count: object?.Images?.length || 0 }),   editMode: true,  confirmEdit: true,  content: <Max2Media /> },
    { key: 'Transfers',   header: t('object.sections.transfers',  { count: object?.Transfers?.length || 0 }),  editMode: true,  confirmEdit: true,  content: <Max2Transfers sectionWidth={sideSectionsWidth} /> },
    { key: 'Contracts',   header: t('object.sections.contracts',  { count: object?.Contracts?.length || 0 }),   editMode: true,  confirmEdit: false, content: <Max2Contracts sectionWidth={sideSectionsWidth} /> },
    { key: 'Exports',     header: t('object.sections.exports',    { pdf: object?.Exports?.PDF?.length || '-', edf: object?.Exports?.EDF?.length || '-' }),     editMode: false, confirmEdit: false, content: <Max2Exports sectionWidth={sideSectionsWidth} /> },
  ];

  // Add suballotment section if allotments exist
  let allotmentsContent = [];
  if (object?.SubAllotments && Object.keys(object.SubAllotments).length > 0) {
    allotmentsContent.push(<Max2Allotments isMainAllotment={true} />);
    Object.keys(object.SubAllotments).forEach(subcode => {
       allotmentsContent.push(<Max2Allotments isSubAllotment={true} subCode={subcode} />);
    });
  } else {
    allotmentsContent.push(<Max2Allotments />);
  }
  availableSections.push({ key: 'Allotments', header: t('object.sections.allotments'), editMode: true,  confirmEdit: false, content: allotmentsContent });

  // Add section history 
  availableSections.push({ key: 'History', header: t('object.sections.history'), editMode: false, confirmEdit: false, content: <Max2History historyData={meta?.History} /> })


  let sections = {};
  let sectionsMinimized = [];


  // Templates for expanded sections
  availableSections.forEach((sectionConfig) => {
    const editModeSection   = stateSections[sectionConfig.key].edit;
    const disabledSection   = stateSections[sectionConfig.key].disabled;
    const maximizedSection  = stateSections[sectionConfig.key].maximized;
    const segmentStyles     = disabledSection ? { margin: '10px', opacity: '0.2' } : { margin: '10px' };

    if (!maximizedSection) {
      sections[sectionConfig.key] = <></>;
    } else {
      sections[sectionConfig.key] = (
        <Segment key={sectionConfig.key} textAlign='left' style={segmentStyles}>
          <Label circular style={{marginBottom: '20px', background: 'transparent'}}><span style={{fontSize: '1.4em', margin: '10px'}}>{sectionConfig.header}</span></Label>
          <div style={{position: 'absolute', top: '5px', right: '5px'}}>
            {sectionConfig.key !== 'Masterdata' && (<Icon link size='large' name='window minimize'  onClick={()=>{ dispatch(minimizeSection(sectionConfig.key)) }} />)}
            {sectionConfig.editMode && editModeSection && 
              <>
              <Icon link size='large' name='times' onClick={()=>handleAbortEditModeClick(sectionConfig.key)} />
                {sectionConfig.confirmEdit && (
                  <Icon link size='large' color='green' name='check' onClick={()=>handleConfirmEditModeClick(sectionConfig.key)} />
                )}
              </>
            }
            {sectionConfig.editMode && !editModeSection && !disabledSection && 
              <Icon link size='large' name='edit' onClick={()=>handleEditModeClick(sectionConfig.key)} />
            }
            {!editModeSection && disabledSection && 
              <></>
            }
          </div>
          {sectionConfig.content}
        </Segment>
      );
    }
  })

  // Templates for minimized sections
  availableSections.forEach((sectionConfig) => {
    const maximizedSection  = stateSections[sectionConfig.key].maximized;

    if (!maximizedSection) {
      sectionsMinimized.push(<Label circular as='a' size='big' key={sectionConfig.key} style={{backgroundColor: 'rgb(255 255 255)'}} onClick={()=>dispatch(maximizeSection(sectionConfig.key))}>&nbsp;&nbsp;{sectionConfig.header}<Icon name='window maximize' style={{marginLeft: '15px'}}/></Label>);
    }
  })

  // loading animation
  if (!isLoadad) {
    return (
      <Segment textAlign='left' style={{margin: '25px'}}>
        <Header as='h2' textAlign='center'>
          <Icon loading name='spinner' />
          <HeaderContent>
            {t('object.loading')}
          </HeaderContent>
        </Header>
      </Segment>
    )
  }

  // calculate contend height
  const contentHeightDelta = dataChanges.length > 0 ? 120 : 70;



  return (
    <>
      {/* Modal für Kopierfunktion */}
      <Modal
        closeIcon
        open={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        size='medium'
      >
        <Header icon='copy outline' content='Objekt kopieren' />
        <Modal.Content>
            {copyResponse 
            ? <Max2CopyObjectResponse response={copyResponse} closeModalFunc={() => {setShowCopyModal(false)}} /> 
            : <>
              {copyObject 
                ? (
                  <Segment>
                    <br />
                    <Header as='h2' textAlign='center'>
                      <Icon loading name='spinner' />
                      <HeaderContent>
                        {t('object.copy.dialog.copying')}
                      </HeaderContent>
                    </Header>
                    <br />
                  </Segment>
                )
                : (
                  <Segment>
                    <Header as='h2' textAlign='center'>
                      <Icon name='cogs' />
                      <HeaderContent>
                        {t('object.copy.dialog.parameters')}
                      </HeaderContent>
                    </Header>
                    <Form>

                      <FormInput label={t('object.copy.fields.code')} value={copyOptions.code} placeholder={object.Product.Code} onChange={(e, data) => setCopyOptions({...copyOptions, code: data.value})} />
                      <FormInput label={t('object.copy.fields.name')} value={copyOptions.name} placeholder={object.Basics.HotelName} onChange={(e, data) => setCopyOptions({...copyOptions, name: data.value})} />
                      <br />
                      <br />
                      <Button basic circular style={{width:'200px', margin: '5px'}} onClick={() => {setShowCopyModal(false); setCopyOptions({code: '', name: ''}); }}>{t('object.copy.button.cancel')}</Button>
                      {copyOptions.code && copyOptions.code.length 
                        ? <Button primary circular floated='right' style={{width:'200px', margin: '5px'}} onClick={() => copyObjectClick(objectID)}>{t('object.copy.button.copy')}</Button> 
                        : <Button disabled primary circular floated='right' style={{width:'200px', margin: '5px'}}>{t('object.copy.button.copy')}</Button>
                      }
                    </Form>
                  </Segment>
                )
              }
              </>
            }
        </Modal.Content>
      </Modal>

      {/* Content für Druckansicht */}
      <div id="print_preview_content" style={{display: 'none'}}>
        <Max2ObjectPrintView object={object} />
      </div>

      {/* Sidebar für Aktionen */}
      <SidebarPushable as={Segment}>
        <Sidebar
          vertical
          as        = {Menu}
          animation = 'overlay'
          direction = 'right'
          icon      = 'labeled'
          visible   = {isSidebar}
          width     = 'wide'
          onHide    = {() => dispatch(hideObjectSidebar())}
        >
        <MenuItem>
            <Container>
              <Divider horizontal>{t('object.sidebar.actions')}</Divider>
              <Button primary circular icon labelPosition='left' style={{width:'200px', margin: '5px'}} onClick={() => { handlePrintViewClick(object) }}><Icon name="print" />{t('object.sidebar.printView')}</Button>
              <Button primary circular icon labelPosition='left' style={{width:'200px', margin: '5px'}} onClick={() => { setCopyResponse(false); setShowCopyModal(true);}}><Icon name="copy" />{t('object.sidebar.copyObject')}</Button>
              <Button basic circular icon labelPosition='left' style={{width:'200px', margin: '5px'}} onClick={() => {}}><Icon name="trash" />{t('object.sidebar.delete')}</Button>
              <br /><br />

              
              <Divider horizontal>{t('object.sections.history')}</Divider>
            </Container>
          </MenuItem>
        </Sidebar>

        <SidebarPusher dimmed={isSidebar}>
          <div style={{height: `calc(100vh - ${contentHeightDelta}px)`, overflowY: 'scroll', overflowX: 'hidden', padding: '0px'}}>
            <Grid style={{marginTop: '-20px'}}>
              <GridRow columns={1}>
                <GridColumn>{sections.Masterdata}</GridColumn>
              </GridRow>
              <GridRow columns={1}>
                <GridColumn>{sectionsMinimized.map((section)=>{ return section; })}</GridColumn>
              </GridRow>
              {mainSectionsMax > 1 ? (
                <GridRow columns={2}>
                  <GridColumn width={12}>
                    {objectID && objectID !== '__new__' && (<>{sections.Texts}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Media}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Allotments}</>)}
                  </GridColumn>
                  <GridColumn width={4}>
                    {objectID && objectID !== '__new__' && (<>{sections.Transfers}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Contracts}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Exports}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.History}</>)}
                  </GridColumn>
                </GridRow>
              ) : (
                <GridRow columns={1}>
                  <GridColumn>
                    {objectID && objectID !== '__new__' && (<>{sections.Texts}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Media}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Allotments}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.History}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Transfers}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Contracts}</>)}
                    {objectID && objectID !== '__new__' && (<>{sections.Exports}</>)}
                  </GridColumn>
                </GridRow>
              )}
            </Grid>
          </div>
        </SidebarPusher>
      </SidebarPushable>
    </>


  );
}

export default Max2PageObject;
